<template>
  <div class="richtexting-ctas ml-2">
    <div class="flex justify-content-between flex-wrap">
      <div class="flex justify-content-center">
        <label class="block my-3">{{ t('templateBuilder.fields.ctaConfiguration') }}</label>
      </div>
    </div>

    <div class="flex align-items-center gap-2">
      <SpmButton
        :label="t('templateBuilder.richTexting.manageCtas')"
        icon="fa-regular fa-hand-pointer"
        class-style="p-button p-button-secondary"
        @click="openModal"
      />
      <span
        v-if="ctasData.length > 0"
        class="richtexting-ctas__badge"
      >
        {{ ctasData.length }}
      </span>
    </div>

    <small
      v-if="limitReachedMessage"
      class="text-orange-600 mt-2 block"
    >
      {{ limitReachedMessage }}
    </small>

    <RichTextingCtasModal
      v-model:visible="isModalVisible"
      :configs="configs"
      :parser-values="parserValues"
      @on-change-properties="handleModalChange"
    />
  </div>
</template>

<script lang="ts">
import { LinkTypeEnum, ParserFieldObject } from '@/types';
import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import RichTextingCtasModal from '@/components/template-builder/modals/RichTextingCtasModal.vue';
import { useI18n } from 'vue-i18n';
import { isRcsSmartListEnabled } from '../getters/rcs-smart-product-list';

interface CtaData {
  translationId: string | null;
  value: string;
  action: {
    type: string;
    value: string;
  };
}

export default defineComponent({
  name: 'RichTextingCtas',

  components: {
    SpmButton,
    RichTextingCtasModal,
  },

  props: {
    configs: {
      type: Object,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, { emit }) {
    const { t } = useI18n();
    const ctasData = ref<CtaData[]>([]);
    const isModalVisible = ref(false);

    // Count buttons by type
    const buttonCounts = computed(() => {
      const counts = {
        callNumber: 0,
        webAddress: 0,
        total: ctasData.value.length,
      };

      ctasData.value.forEach((cta) => {
        if (cta.action.type === LinkTypeEnum.CALL_NUMBER) {
          counts.callNumber += 1;
        } else if (cta.action.type === LinkTypeEnum.WEB_ADDRESS) {
          counts.webAddress += 1;
        }
      });

      return counts;
    });

    // Generate appropriate limit message
    const limitReachedMessage = computed(() => {
      if (isRcsSmartListEnabled() && buttonCounts.value.total >= 1) {
        return t('templateBuilder.richTexting.maxButtonsSmartlist');
      }
      if (buttonCounts.value.total >= 3) {
        return t('templateBuilder.richTexting.maxButtonsReached');
      }
      if (buttonCounts.value.callNumber >= 1 && buttonCounts.value.webAddress >= 2) {
        return t('templateBuilder.richTexting.maxButtonTypesReached');
      }
      return null;
    });

    // Watch for changes in parser values and update local state
    watch(
      () => props.parserValues.properties[0].value,
      (newValue) => {
        if (Array.isArray(newValue)) {
          ctasData.value = JSON.parse(JSON.stringify(newValue));
        }
      },
      { immediate: true, deep: true },
    );

    const openModal = () => {
      isModalVisible.value = true;
    };

    const handleModalChange = (payload: any) => {
      emit('on-change-properties', payload);
    };

    return {
      t,
      ctasData,
      isModalVisible,
      limitReachedMessage,
      openModal,
      handleModalChange,
    };
  },
});
</script>

<style lang="scss" scoped>
.richtexting-ctas {
  padding-bottom: 1.5rem;

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 8px;
    background: var(--primary-color, #3b82f6);
    color: white;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: 600;
  }
}
</style>
